import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Shield, 
  Wrench, 
  FileCheck, 
  Clock, 
  HardHat,
  ArrowRight,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ServiziPage = () => {
  const services = [
    {
      id: 'linee-vita',
      icon: <Shield className="w-10 h-10" />,
      title: 'Linee Vita',
      subtitle: 'Sistemi Anticaduta Tipo C',
      description: 'Progettiamo e installiamo linee vita permanenti conformi alla norma UNI EN 795. I nostri sistemi garantiscono la massima sicurezza per tutti gli operatori che accedono alle coperture.',
      features: [
        'Linee vita orizzontali flessibili',
        'Sistemi per coperture piane e inclinate',
        'Ancoraggi puntuali tipo A',
        'Dispositivi retrattili tipo C',
        'Certificazione UNI EN 795'
      ],
      image: 'https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/z20hyi6z_OIP%20%283%29.jpg'
    },
    {
      id: 'parapetti',
      icon: <Wrench className="w-10 h-10" />,
      title: 'Parapetti Anticaduta',
      subtitle: 'Protezione Collettiva',
      description: 'I parapetti rappresentano la soluzione di protezione collettiva più efficace. Offriamo sistemi modulari in alluminio con fissaggio rapido e minimo impatto estetico.',
      features: [
        'Parapetti fissi e removibili',
        'Sistemi autoportanti',
        'Fissaggio a muro, a terra, su lamiera',
        'Conformità UNI EN 13374',
        'Installazione rapida'
      ],
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800'
    },
    {
      id: 'progettazione',
      icon: <FileCheck className="w-10 h-10" />,
      title: 'Progettazione',
      subtitle: 'Analisi e Design Su Misura',
      description: 'Ogni copertura ha caratteristiche uniche. I nostri progettisti analizzano le specificità strutturali e funzionali per definire il sistema anticaduta ottimale.',
      features: [
        'Sopralluogo tecnico gratuito',
        'Analisi strutturale del supporto',
        'Elaborazione progetto esecutivo',
        'Relazione tecnica e calcoli',
        'Supporto pratiche edilizie'
      ],
      image: 'https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/5az7lvks_linee-vita-installazione-ottimale.jpg'
    },
    {
      id: 'installazione',
      icon: <HardHat className="w-10 h-10" />,
      title: 'Installazione',
      subtitle: 'Montaggio Professionale',
      description: 'Il nostro team di installatori certificati UNI 11900 garantisce un montaggio a regola d\'arte, con documentazione completa e formazione per gli utilizzatori.',
      features: [
        'Installatori certificati UNI 11900',
        'Attrezzature professionali',
        'Documentazione fotografica',
        'Dichiarazione di corretta installazione',
        'Formazione agli utilizzatori'
      ],
      image: 'https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/b1xvhigq_OIP%20%282%29.jpg'
    },
    {
      id: 'revisione',
      icon: <Clock className="w-10 h-10" />,
      title: 'Revisione Periodica',
      subtitle: 'Manutenzione Obbligatoria',
      description: 'La revisione periodica è un obbligo normativo. Offriamo un servizio completo di ispezione, manutenzione e certificazione per garantire l\'efficienza del sistema nel tempo.',
      features: [
        'Ispezione visiva e funzionale',
        'Verifica serraggio e usura',
        'Sostituzione componenti usurati',
        'Aggiornamento libretto',
        'Report dettagliato'
      ],
      image: 'https://images.unsplash.com/photo-1581094794329-cd675335442f?w=800'
    }
  ];

  return (
    <div className="bg-revo-bg pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              I Nostri Servizi
            </span>
            <h1 className="font-barlow text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
              Sicurezza<br />
              <span className="text-gray-500">A 360°</span>
            </h1>
            <p className="font-manrope text-lg text-gray-300 leading-relaxed">
              Dalla progettazione all'installazione, dalla certificazione alla manutenzione: 
              un servizio completo per la sicurezza dei lavori in quota.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      {services.map((service, index) => (
        <Section 
          key={service.id} 
          className={`py-24 ${index % 2 === 0 ? 'bg-revo-bg' : 'bg-revo-paper'}`}
        >
          <div id={service.id} className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-16 h-16 bg-revo-orange/10 border border-revo-orange/20 flex items-center justify-center text-revo-orange mb-6">
                  {service.icon}
                </div>
                <span className="font-manrope text-sm text-gray-500 uppercase tracking-widest">
                  {service.subtitle}
                </span>
                <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mt-2 mb-6">
                  {service.title}
                </h2>
                <p className="font-manrope text-gray-400 leading-relaxed mb-8">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-revo-orange flex-shrink-0" />
                      <span className="font-manrope text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contatti"
                  data-testid={`service-cta-${service.id}`}
                  className="inline-flex items-center gap-3 bg-revo-orange text-white px-6 py-3 font-barlow font-bold uppercase tracking-widest text-sm hover:bg-revo-orange-dark transition-all duration-300"
                >
                  Richiedi Info
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Image */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="aspect-[4/3] overflow-hidden border border-revo-border">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-revo-orange/30" />
              </div>
            </div>
          </div>
        </Section>
      ))}

      {/* CTA Section */}
      <Section className="py-24 md:py-32 bg-revo-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Hai Bisogno di Un<br />
            <span className="text-revo-orange">Preventivo?</span>
          </h2>
          <p className="font-manrope text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            Utilizza il nostro configuratore per ricevere un preventivo personalizzato 
            in base alle caratteristiche della tua copertura.
          </p>
          <Link
            to="/contatti"
            data-testid="services-final-cta"
            className="inline-flex items-center gap-3 bg-revo-orange text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,102,0,0.4)]"
          >
            Configura il Tuo Preventivo
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </Section>
    </div>
  );
};

export default ServiziPage;
